/**
 * <CorrectionPause> — une pause pendant que Sam corrige la copie, quand le défi n'a pas de
 * question pour l'élève (organism).
 *
 * Les trois pauses de l'ancien code (`PendingAction.js`), tirées une fois par copie :
 *  - `breathing` : « Pause zen », une bulle, « Inspire… » / « Expire… », « Suis la bulle et
 *    respire » ;
 *  - `tongue` : « Vire-langue », « Répète à voix haute », la phrase, « Un autre » ;
 *  - `fact` : une anecdote, une blague ou une citation (son intitulé dit laquelle), « Encore ».
 *    Le texte peut mettre des mots en gras (`**…**`), comme aujourd'hui.
 *
 * Contrôlé : la phrase montrée arrive en props ; « Un autre » et « Encore » remontent. La pause zen
 * **respire** (Wajih, 20/10) : la bulle grandit à l'inspiration et rapetisse à l'expiration, en
 * douceur, à la courbe de la marque, chaque temps à la durée de `breath.half` (`theme/tokens/motion`),
 * et le mot du temps change avec elle (`hooks/useBreathing`). Le rythme est un état d'interface : il vit
 * ici, pas dans l'écran. En mouvement réduit, elle reste immobile, à sa taille d'inspiration, et dit
 * ses deux temps d'un coup. Ce qui change : les emojis de l'ancien code (la feuille, l'ampoule, le
 * rire, l'étincelle, le globe) ne sont pas repris ; la feuille de la pause zen devient le glyphe
 * `seedling` de l'interface.
 */
import React, { useMemo } from "react"
import { Animated, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { useBreathing } from "../../hooks/useBreathing"
import { breath as breathRhythm } from "../../theme/tokens/motion"

export type CorrectionPauseProps =
    | { kind: "breathing"; testID?: string }
    | { kind: "tongue"; phrase: string; onNext: () => void; testID?: string }
    | {
          kind: "fact"
          /** « Le savais-tu ? », « Blague du jour », « Citation », « Info générale ». */
          label: string
          /** Le texte, ses mots forts entre `**`. */
          text: string
          onNext: () => void
          testID?: string
      }

/** La bulle de la pause zen, à l'inspiration ; l'expiration la ramène à 80 %. */
const BUBBLE_SIZE = 120
const BUBBLE_OUT = 0.8

const TEXTS = {
    breathing: "Pause zen",
    breathIn: "Inspire…",
    breathOut: "Expire…",
    breathHint: "Suis la bulle et respire",
    /** Mouvement réduit : la bulle ne bouge pas, la pause dit ses deux temps d'un coup. */
    breathStill: "Inspire… Expire…",
    breathStillHint: "Respire à ton rythme",
    tongue: "Vire-langue",
    tongueHint: "Répète à voix haute",
    tongueNext: "Un autre",
    factNext: "Encore",
}

/** « Un **poulpe** a… » → les morceaux, gras ou non. */
function emphasisParts(text: string): { text: string; strong: boolean }[] {
    return text
        .split(/(\*\*[^*]+\*\*)/g)
        .filter((part) => part !== "")
        .map((part) =>
            part.startsWith("**") && part.endsWith("**")
                ? { text: part.slice(2, -2), strong: true }
                : { text: part, strong: false }
        )
}

/** La pause zen : la bulle respire, le mot du temps la suit. */
function Breathing({ testID }: { testID?: string }) {
    const { breath, progress, still } = useBreathing(breathRhythm.half)
    // Une seule interpolation : une nouvelle à chaque temps rattacherait la bulle au pilote natif.
    const scale = useMemo(() => progress.interpolate({ inputRange: [0, 1], outputRange: [BUBBLE_OUT, 1] }), [progress])
    return (
        <Stack gap="4" align="center" testID={testID}>
            <SectionLabel>{TEXTS.breathing}</SectionLabel>
            <View style={styles.bubbleBox}>
                <Animated.View style={[styles.bubble, { transform: [{ scale }] }]}>
                    <Icon name="seedling" size={24} color="success" />
                </Animated.View>
            </View>
            <Stack gap="1" align="center">
                <Text size="text-2xl" weight="bold" color="text-strong">
                    {still ? TEXTS.breathStill : breath === "out" ? TEXTS.breathOut : TEXTS.breathIn}
                </Text>
                <Text size="text-md" color="text-muted">
                    {still ? TEXTS.breathStillHint : TEXTS.breathHint}
                </Text>
            </Stack>
        </Stack>
    )
}

export function CorrectionPause(props: CorrectionPauseProps) {
    if (props.kind === "breathing") return <Breathing testID={props.testID} />

    if (props.kind === "tongue") {
        return (
            <Stack gap="3" align="center" testID={props.testID}>
                <Stack gap="1" align="center">
                    <SectionLabel>{TEXTS.tongue}</SectionLabel>
                    <Text size="text-sm" color="text-muted">
                        {TEXTS.tongueHint}
                    </Text>
                </Stack>
                <Text size="text-xl" weight="bold" color="text-strong" center>
                    {`« ${props.phrase} »`}
                </Text>
                <Button label={TEXTS.tongueNext} onPress={props.onNext} fullWidth />
            </Stack>
        )
    }

    return (
        <Stack gap="3" align="center" testID={props.testID}>
            <SectionLabel>{props.label}</SectionLabel>
            <Text size="text-lg" color="text-primary" center>
                {emphasisParts(props.text).map((part, index) =>
                    part.strong ? (
                        <Text key={index} size="text-lg" weight="bold" color="text-strong">
                            {part.text}
                        </Text>
                    ) : (
                        part.text
                    )
                )}
            </Text>
            <Button label={TEXTS.factNext} onPress={props.onNext} fullWidth />
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** La place de la bulle à l'inspiration : à l'expiration, rien ne bouge autour. */
    bubbleBox: {
        width: BUBBLE_SIZE,
        height: BUBBLE_SIZE,
        alignItems: "center",
        justifyContent: "center",
    },
    /** La bulle à l'inspiration ; l'échelle la ramène à l'expiration. */
    bubble: {
        width: BUBBLE_SIZE,
        height: BUBBLE_SIZE,
        borderRadius: BUBBLE_SIZE / 2,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.colors.successLight,
    },
}))
